import React, { useState, useEffect } from "react";
import { View, StyleSheet, Platform, Linking } from "react-native";
import variables from "../../../styles/variables";
import { LoadingState } from "../../States/states";
import RText from "../../RText";
import RImage from "../../RImage/RImage";
import { APIResponse } from "../../../types/responses/APIResponse";
import { fetchGet } from "../../../client";
import { BettingTile } from "../../../types/bettingTile";
import RButton from "../../Buttons/RButton";
// @ts-ignore
import AnimatedDropdownWrapper from "../../Animations/DropdownAnimation";
import Icon from "../../Icon";
import { RTextColors } from "../../RText/RText";
import { RLink } from "../../RLink/RLink";
import CmsContent from "../../cms/CmsContent";

const { spacing } = variables;
import { useAtom } from "jotai";
import { deviceTypeAtom } from "../../../atoms";
import { LinearGradient } from "expo-linear-gradient";
import FullStory from "../../../services/FullStory";
import { logEvent } from "../../../client/analytics/trackPageView";
import Gap from "../../Gap";

type Props = {
  id?: string | number;
  hasData?: boolean;
  data?: BettingTile;
  isGrouped?: boolean;
  topOffer?: boolean;
  isRelatedContent?: boolean;
};

/**
 * RBettingTile component displays a bookmaker betting tile with a title, description,
 * bookmaker logo, Terms and Conditions button, and a Claim Now button that is customisable in the CMS.
 *
 * It is one of the RAttachment's that can be added to page content in the CMS.
 *
 * It's very similar to ROffer but the designs are different, and it's also used in more
 * places across the app (i.e. on the news article and racecard pages)
 *
 * @param {string \ number} id - The ID of the betting tile to fetch.
 * @param {boolean} hasData - Indicates if data is already provided.
 * @param {BettingTile} data - The betting tile data to display.
 * @param {boolean} isGrouped - Whether the tile is part of a Betting Tile list.
 * @param {boolean} topOffer - Whether the tile is a "top offer" (special styling and behaviour).
 * @param {boolean} isRelatedContent - Whether the tile is being displayed as related content.
 *
 * @returns {JSX.Element | null} The rendered RBettingTile component or null if loading/error.
 */

const RBettingTile = ({
  id,
  hasData = false,
  data = null,
  isGrouped,
  topOffer = false,
  isRelatedContent = false,
}: Props) => {
  const [bettingTile, setBettingTile] = useState<BettingTile>(null);
  const [openTerms, setOpenTerms] = useState<boolean>(topOffer);
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(!hasData);

  const isIOS = Platform.OS === "ios";

  const isWeb = Platform.OS === "web";
  const platform = isWeb ? "web" : "mobile";

  //Apply same behaviour on mobile web as native mobile
  const isNativeMobile = Platform.OS === "android" || isIOS;
  const isWebMobile =
    Platform.OS === "web" &&
    typeof window !== "undefined" &&
    window?.matchMedia("(pointer: coarse)")?.matches;

  const isMobile = isNativeMobile || isWebMobile;

  const [deviceType] = useAtom(deviceTypeAtom);
  const isTablet = deviceType === "TABLET";
  const showChevron = isGrouped && isTablet && isRelatedContent;

  useEffect(() => {
    if (hasData) {
      setBettingTile(data);
      setLoading(false);
      return;
    }
    if (!id && !hasData) {
      setError("No betting tile ID provided");
      setLoading(false);
      return;
    }
    const fetchData = async () => {
      try {
        const bettingTilesRes: APIResponse<any> = await fetchGet(
          `betting-tiles/attachment?id=${id}`
        );
        setBettingTile(bettingTilesRes?.betting_tiles?.[0] ?? null);
      } catch (err) {
        setError("Error loading betting tiles." + err?.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  if (loading)
    return (
      <View style={styles.container}>
        <LoadingState name="RBettingTileLoader" />
      </View>
    );

  if (!bettingTile || error) return null;

  const Wrapper = topOffer ? LinearGradient : View;

  // Corner > corner gradient when top offer
  const wrapperProps = topOffer
    ? {
        colors: [
          bettingTile.background_color,
          bettingTile.background_color,
          lightenColor(bettingTile.background_color),
        ],
        locations: [0, 0.4, 1],
        start: { x: 0, y: 0 },
        end: { x: 1, y: 1 },
      }
    : {};

  const sendFSEvent = (name: string) => {
    logEvent({
      key: name,
      data: {
        tileId: bettingTile?.id,
        bookmaker: bettingTile?.bookmaker?.name,
        text: bettingTile?.text,
        platform: platform,
        topOffer: topOffer,
      },
    });
  };

  return (
    <Wrapper
      {...wrapperProps}
      style={[
        styles.container,
        {
          backgroundColor: !topOffer
            ? bettingTile.background_color || "transparent"
            : undefined,
          marginBottom: isGrouped ? spacing.xxsmall : undefined,
          display: "flex", //this is automatically being set as 'inline-flex' which is creating unnecessary space above the component
        },
      ]}
      //WEB BEHAVIOUR
      // @ts-ignore
      onMouseEnter={() => {
        if (isMobile || isTablet) return;
        setOpenTerms(true);
        sendFSEvent("betting_tile_open_terms");
      }}
      onMouseLeave={() => {
        if (isMobile || isTablet) return;
        if (!topOffer) setOpenTerms(false);
      }}
      onClick={() => {
        if (isMobile || isTablet) return;
        sendFSEvent("betting_tile_open_link");
        if (bettingTile.link) window.open(bettingTile.link, "_blank");
      }}
      //MOBILE BEHAVIOUR
      onStartShouldSetResponder={() => isMobile || isTablet}
      onResponderRelease={() => {
        if (!(isMobile || isTablet)) return;

        if (!openTerms) {
          // First tap → expand
          setOpenTerms(true);
          sendFSEvent("betting_tile_open_terms");
        } else {
          // Second tap → open link
          if (bettingTile.link) {
            Linking.openURL(bettingTile.link);
            sendFSEvent("betting_tile_open_link");
          }
        }
      }}
    >
      <View style={styles.content}>
        <View style={styles.logoAndTextContainer}>
          {/*Logo*/}
          <RImage
            style={[styles.bmLogo]}
            source={bettingTile.bookmaker?.logo_square?.placeholder_image_url}
          />

          {/*Text and T&Cs*/}
          <View style={styles.text}>
            <RText
              weight="bold"
              color={bettingTile.text_color || "primary"}
              lineHeight={16}
            >
              {bettingTile.text}
            </RText>
            <RText
              color={bettingTile.text_color || "primary"}
              style={{
                textDecorationLine: "underline",
                lineHeight: 12,
              }}
              size="xxs_xs"
            >
              T&Cs Apply
            </RText>
          </View>
        </View>
        {/*Button - unexpanded view*/}
        {!openTerms ? (
          <View style={[styles.btnContainer]}>
            {showChevron ? (
              <View style={{ maxHeight: "100%", top: isIOS ? 2 : 0 }}>
                <RLink href={bettingTile.link || "#"}>
                  <Icon
                    name="chevronRightNoColor"
                    size="small"
                    color={bettingTile.text_color || "primary"}
                  />
                </RLink>
              </View>
            ) : (
              <ClaimButton
                color={bettingTile.text_color || "primary"}
                onPress={() => setOpenTerms(true)}
              />
            )}
          </View>
        ) : (
          // Placeholder to keep alignment when expanded
          <View style={{ width: showChevron ? 16 : 100 }} />
        )}
      </View>
      <AnimatedDropdownWrapper open={openTerms}>
        <View style={styles.wrapper}>
          <CmsContent
            content={bettingTile.description || ""}
            textColor={bettingTile.text_color || "primary"}
            textSize="xxs_xs"
            style={isTablet ? { marginBottom: 8 } : {}}
            allContent={false}
          />
          {/*Button - expanded view*/}

          {openTerms && (
            <View>
              <ClaimButton
                color={bettingTile.text_color || "primary"}
                expandedView
                onPress={() => {
                  if (bettingTile.link) {
                    if (isWeb) sendFSEvent("betting_tile_open_link");
                    Linking.openURL(bettingTile.link);
                  }
                }}
              />
            </View>
          )}
        </View>
      </AnimatedDropdownWrapper>
    </Wrapper>
  );
};

const ClaimButton = ({
  color,
  expandedView = false,
  onPress,
}: {
  color?: RTextColors | string;
  expandedView?: boolean;
  onPress?: () => void;
}) => (
  <RButton
    title="Claim Now"
    backgroundColorOverride="transparent"
    borderStyle={{
      borderColor: color || "white",
      borderStyle: "solid",
      borderWidth: 1.5,
    }}
    alignItems="stretch"
    textColorOverride={color || "white"}
    minWidth={expandedView ? "100%" : undefined}
    onPress={onPress}
  />
);

const lightenColor = (hex: string, amount = 0.2) => {
  if (!hex?.startsWith("#")) return hex;

  const num = parseInt(hex.replace("#", ""), 16);

  const r = Math.min(255, (num >> 16) + 255 * amount);
  const g = Math.min(255, ((num >> 8) & 0x00ff) + 255 * amount);
  const b = Math.min(255, (num & 0x0000ff) + 255 * amount);

  return `rgb(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)})`;
};

export default RBettingTile;

const styles = StyleSheet.create({
  container: {
    minWidth: "100%",
    borderRadius: 8,
    overflow: "hidden",
    paddingHorizontal: 8,
  },
  content: {
    flexDirection: "row",
    justifyContent: "space-between",
    minHeight: 64,
  },
  logoAndTextContainer: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
  },
  text: {
    padding: 8,
    flex: 1,
  },
  bmLogo: {
    aspectRatio: 1 / 1,
    width: 48,
    height: 48,
    borderRadius: 8,
  },
  btnContainer: {
    alignItems: "flex-end",
    justifyContent: "center",
    paddingVertical: 8,
  },
  wrapper: {
    paddingBottom: 8,
    paddingHorizontal: 8,
  },
});
